import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, usePurchaseApi } from './purchaseApi';
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import type { AfterAcceptanceContext } from './PublicQuote';

/**
 * Board #593: life, where the insurer has no payment API and the customer finishes on its own site.
 *
 *   1. the life proposal (life assured, address, nominees, appointee for a minor nominee) is filed
 *      with `POST /life-proposal/`;
 *   2. `POST /proceed/` sends the application and answers `handoff` with the insurer's personal
 *      link (it carries the customer's mobile and date of birth, so it is only ever shown as the
 *      button's target and never written anywhere on this page);
 *   3. nothing on this page claims the customer has paid, that a policy exists or that the insurer
 *      has approved: the page says what happens next and where.
 */

interface Vocabulary {
  titles: string[];
  genders: string[];
  marital_status: string[];
  education: string[];
  occupation: string[];
  nature_of_work: string[];
  fund_sources: string[];
  nominee_relations: string[];
  appointee_relations: string[];
  max_nominees: number;
}

interface FormMeta {
  prefill: { first_name: string; last_name: string };
  vocabulary: Vocabulary;
}

interface RedirectForm {
  action: string;
  method: 'POST';
  fields: Record<string, string>;
}

interface HandoffResult {
  next_step: 'handoff' | 'details' | 'wait' | 'offline' | 'done' | string;
  message: string;
  redirect_url: string;
  /** #585: a browser Form-POST to the insurer's page, in place of `redirect_url`. */
  redirect_form?: RedirectForm;
}

interface Person {
  title: string;
  marital_status: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
}
interface NomineeDraft extends Person {
  relationship: string;
  share_pct: string;
  appointee: (Person & { relationship: string }) | null;
}

const emptyPerson = (): Person => ({ title: '', marital_status: '', first_name: '', last_name: '', date_of_birth: '', gender: '' });
const emptyNominee = (): NomineeDraft => ({ ...emptyPerson(), relationship: '', share_pct: '', appointee: null });

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';
const primaryButton =
  'px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButton =
  'px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

const words = (w: string) => w.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export function isMinor(dob: string, today = new Date()): boolean {
  if (!dob) return false;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return false;
  let age = today.getFullYear() - d.getFullYear();
  const beforeBirthday =
    today.getMonth() < d.getMonth() || (today.getMonth() === d.getMonth() && today.getDate() < d.getDate());
  if (beforeBirthday) age -= 1;
  return age < 18;
}

/**
 * Board #585 (India First Life, model 2): the insurer's page takes our quote inputs by Form-POST and the
 * customer finishes there. Nothing is collected or filed here. One button asks the server for the form,
 * then the browser posts it (a real form, so the details never sit in a link or a log).
 */
function FormPostPanel({ ctx }: { ctx: AfterAcceptanceContext }) {
  const api = usePurchaseApi();
  const insurerName = ctx.acceptedOption.insurer_name;
  const [result, setResult] = useState<HandoffResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const form = result?.next_step === 'handoff' && result.redirect_form?.action.startsWith('https://') ? result.redirect_form : null;

  useEffect(() => {
    if (form) {
      try {
        formRef.current?.submit();
      } catch {
        // The visible button below posts the same form.
      }
    }
  }, [form]);

  const proceed = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost(api, `${api.base(ctx.token)}/proceed/`, {});
      setResult(res.data as HandoffResult);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Buy this policy" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 sm:p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Buy {insurerName}</h2>
      {form ? (
        <form ref={formRef} method="POST" action={form.action} className="space-y-2">
          {Object.entries(form.fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
          <p role="status" className="text-sm text-gray-800 dark:text-gray-200">
            {result?.message}
          </p>
          <button type="submit" className={primaryButton}>
            Continue on {insurerName}
          </button>
        </form>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            You will finish on {insurerName}&apos;s own site: your details, documents, medical check and payment all happen
            there. We pass on the plan you chose; you can change anything on their page.
          </p>
          {result && result.next_step !== 'handoff' && (
            <p role="status" className="text-sm text-gray-700 dark:text-gray-300">
              {result.message}
            </p>
          )}
          <button type="button" className={primaryButton} disabled={busy} onClick={() => void proceed()}>
            {busy ? 'Please wait…' : `Continue on ${insurerName}`}
          </button>
        </div>
      )}
      <p className="text-xs text-gray-500">
        Nothing is paid or issued until {insurerName} confirm it; your advisor will update this page when they do.
      </p>
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

export function LifeHandoffPanel({ ctx }: { ctx: AfterAcceptanceContext }) {
  if (ctx.quote.purchase?.form_post === true) return <FormPostPanel ctx={ctx} />;
  return <LifeLinkHandoffPanel ctx={ctx} />;
}

function LifeLinkHandoffPanel({ ctx }: { ctx: AfterAcceptanceContext }) {
  const { quote, token, acceptedOption } = ctx;
  const api = usePurchaseApi();
  const life = quote.purchase?.life;
  const insurerName = acceptedOption.insurer_name;
  const [editing, setEditing] = useState(!life?.submitted);
  const [result, setResult] = useState<HandoffResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proceed = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost(api, `${api.base(token)}/proceed/`, {});
      const answer = res.data as HandoffResult;
      setResult(answer);
      if (answer.next_step === 'details') setEditing(true);
      return answer;
    } catch (err) {
      setError(getErrorMessage(err));
      return null;
    } finally {
      setBusy(false);
    }
  }, [api, token]);

  const saved = useCallback(async () => {
    setEditing(false);
    await ctx.reload();
  }, [ctx]);

  return (
    <section aria-label="Buy this policy" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 sm:p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Buy {insurerName}</h2>
      {life?.sent_to_insurer ? null : editing ? (
        <LifeProposalForm
          token={token}
          insurerName={insurerName}
          notice={result?.next_step === 'details' ? result.message : undefined}
          onSaved={() => void saved()}
          onCancel={life?.submitted ? () => setEditing(false) : undefined}
        />
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-green-700 dark:text-green-400">Your details are saved.</p>
          <ul className="text-sm text-gray-700 dark:text-gray-300">
            {life?.parties.map((p, i) => (
              <li key={`${p.role}-${i}`}>
                {words(p.role)}: {p.name}
                {p.share_pct ? ` (${p.share_pct}%)` : ''}
                {p.pan_masked ? ` · PAN ${p.pan_masked}` : ''}
              </li>
            ))}
          </ul>
          <button type="button" className={secondaryButton} onClick={() => setEditing(true)}>
            Edit details
          </button>
        </div>
      )}

      {!editing && (
        <div className="space-y-2">
          {result?.next_step === 'handoff' ? (
            <>
              <p role="status" className="text-sm text-gray-800 dark:text-gray-200">
                {result.message}
              </p>
              <a href={result.redirect_url} className={`${primaryButton} inline-block`}>
                Continue on {insurerName}
              </a>
              <p className="text-xs text-gray-500">
                {insurerName} take the payment and issue the policy on their site. Nothing is paid or issued until
                they confirm it; your advisor will update this page when they do.
              </p>
            </>
          ) : (
            <>
              {result && result.next_step !== 'details' && (
                <p role="status" className="text-sm text-gray-700 dark:text-gray-300">
                  {result.message}
                </p>
              )}
              <button type="button" className={primaryButton} disabled={busy} onClick={() => void proceed()}>
                {busy ? 'Please wait…' : `Send my application to ${insurerName}`}
              </button>
            </>
          )}
        </div>
      )}

      {life?.sent_to_insurer && !result && (
        <div className="space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">Your application is with {insurerName}.</p>
          <button type="button" className={primaryButton} disabled={busy} onClick={() => void proceed()}>
            {busy ? 'Please wait…' : `Continue on ${insurerName}`}
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Select({
  id,
  value,
  options,
  onChange,
}: {
  id: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <select id={id} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {words(o)}
        </option>
      ))}
    </select>
  );
}

export function LifeProposalForm({
  token,
  insurerName,
  notice,
  onSaved,
  onCancel,
}: {
  token: string;
  insurerName: string;
  notice?: string;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const api = usePurchaseApi();
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [la, setLa] = useState<Record<string, string | boolean>>({
    title: '', first_name: '', last_name: '', gender: '', marital_status: '', father_name: '', mother_name: '',
    spouse_name: '', birthplace: '', nationality_indian: true, is_nri: false, is_pep: false, education: '',
    occupation: '', employer_name: '', designation: '', nature_of_work: '', annual_income: '', pan: '',
  });
  const [address, setAddress] = useState({ house: '', street: '', landmark: '', city: '', state: '', pincode: '' });
  const [funds, setFunds] = useState([{ source: 'salary', percent: '100' }]);
  const [nominees, setNominees] = useState<NomineeDraft[]>([emptyNominee()]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    apiGet(api, `${api.base(token)}/life-proposal/`)
      .then((res) => {
        if (!live) return;
        const data = res.data as FormMeta;
        setMeta(data);
        setLa((cur) => ({ ...cur, first_name: data.prefill.first_name, last_name: data.prefill.last_name }));
      })
      .catch((err) => live && setMetaError(getErrorMessage(err)));
    return () => {
      live = false;
    };
  }, [api, token]);

  if (metaError) {
    return <p className="text-sm text-red-600" role="alert">We could not open the form just now. {metaError}</p>;
  }
  if (!meta) return <p className="text-sm text-gray-500">Loading…</p>;
  const v = meta.vocabulary;

  const setL = (k: string, val: string | boolean) => setLa((cur) => ({ ...cur, [k]: val }));
  const setNominee = (i: number, patch: Partial<NomineeDraft>) =>
    setNominees((cur) =>
      cur.map((n, idx) => {
        if (idx !== i) return n;
        const next = { ...n, ...patch };
        const minor = isMinor(next.date_of_birth);
        next.appointee = minor ? (next.appointee ?? { ...emptyPerson(), relationship: '' }) : null;
        return next;
      })
    );
  const err = (path: string): string | undefined => {
    const hit = path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), errors);
    if (Array.isArray(hit)) return typeof hit[0] === 'string' ? hit[0] : undefined;
    return typeof hit === 'string' ? hit : undefined;
  };

  const shareTotal = nominees.reduce((sum, n) => sum + (Number(n.share_pct) || 0), 0);
  const fundTotal = funds.reduce((sum, f) => sum + (Number(f.percent) || 0), 0);

  const submit = async () => {
    setBusy(true);
    setFormError(null);
    setErrors({});
    try {
      await apiPost(api, `${api.base(token)}/life-proposal/`, {
        life_assured: {
          ...la,
          pan: String(la.pan).trim().toUpperCase(),
          address,
          fund_sources: funds.map((f) => ({ source: f.source, percent: Number(f.percent) })),
        },
        nominees: nominees.map((n) => ({
          first_name: n.first_name,
          last_name: n.last_name,
          title: n.title,
          marital_status: n.marital_status,
          date_of_birth: n.date_of_birth,
          gender: n.gender,
          relationship: n.relationship,
          share_pct: n.share_pct,
          ...(n.appointee ? { appointee: n.appointee } : {}),
        })),
      });
      onSaved();
    } catch (e) {
      const fields = getFieldErrors(e);
      const nonField = fields[NON_FIELD_KEY];
      // The API wraps field errors: { error: { fields: { life_assured: { pan: [...] } } } }.
      const body = (e as { response?: { data?: { error?: { fields?: Record<string, unknown> } } } }).response?.data;
      setErrors(body?.error?.fields ?? {});
      setFormError(
        Array.isArray(nonField) && nonField.length ? String(nonField[0]) : 'Please check the highlighted details.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      aria-label="Life proposal"
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-gray-500">
        {insurerName} need these to issue your policy. Nothing is sent to them until you press the button on the next step.
      </p>
      {notice && <p className="text-sm text-amber-800 dark:text-amber-300">{notice}</p>}

      <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Life assured</legend>
        <Field id="la-title" label="Title" error={err('life_assured.title')}>
          <Select id="la-title" value={String(la.title)} options={v.titles} onChange={(x) => setL('title', x)} />
        </Field>
        <Field id="la-gender" label="Gender" error={err('life_assured.gender')}>
          <Select id="la-gender" value={String(la.gender)} options={v.genders} onChange={(x) => setL('gender', x)} />
        </Field>
        <Field id="la-first" label="First name" error={err('life_assured.first_name')}>
          <input id="la-first" className={inputClass} value={String(la.first_name)} onChange={(e) => setL('first_name', e.target.value)} />
        </Field>
        <Field id="la-last" label="Last name" error={err('life_assured.last_name')}>
          <input id="la-last" className={inputClass} value={String(la.last_name)} onChange={(e) => setL('last_name', e.target.value)} />
        </Field>
        <Field id="la-marital" label="Marital status" error={err('life_assured.marital_status')}>
          <Select id="la-marital" value={String(la.marital_status)} options={v.marital_status} onChange={(x) => setL('marital_status', x)} />
        </Field>
        <Field id="la-spouse" label={la.marital_status === 'married' ? 'Spouse name' : 'Spouse name (if any)'} error={err('life_assured.spouse_name')}>
          <input id="la-spouse" className={inputClass} value={String(la.spouse_name)} onChange={(e) => setL('spouse_name', e.target.value)} />
        </Field>
        <Field id="la-father" label="Father's name" error={err('life_assured.father_name')}>
          <input id="la-father" className={inputClass} value={String(la.father_name)} onChange={(e) => setL('father_name', e.target.value)} />
        </Field>
        <Field id="la-mother" label="Mother's name" error={err('life_assured.mother_name')}>
          <input id="la-mother" className={inputClass} value={String(la.mother_name)} onChange={(e) => setL('mother_name', e.target.value)} />
        </Field>
        <Field id="la-birthplace" label="Place of birth" error={err('life_assured.birthplace')}>
          <input id="la-birthplace" className={inputClass} value={String(la.birthplace)} onChange={(e) => setL('birthplace', e.target.value)} />
        </Field>
        <Field id="la-pan" label="PAN" error={err('life_assured.pan')}>
          <input id="la-pan" className={inputClass} maxLength={10} autoComplete="off" value={String(la.pan)} onChange={(e) => setL('pan', e.target.value.toUpperCase())} />
        </Field>
        <Field id="la-education" label="Education" error={err('life_assured.education')}>
          <Select id="la-education" value={String(la.education)} options={v.education} onChange={(x) => setL('education', x)} />
        </Field>
        <Field id="la-occupation" label="Occupation" error={err('life_assured.occupation')}>
          <Select id="la-occupation" value={String(la.occupation)} options={v.occupation} onChange={(x) => setL('occupation', x)} />
        </Field>
        <Field id="la-employer" label="Employer" error={err('life_assured.employer_name')}>
          <input id="la-employer" className={inputClass} value={String(la.employer_name)} onChange={(e) => setL('employer_name', e.target.value)} />
        </Field>
        <Field id="la-designation" label="Designation" error={err('life_assured.designation')}>
          <input id="la-designation" className={inputClass} value={String(la.designation)} onChange={(e) => setL('designation', e.target.value)} />
        </Field>
        <Field id="la-work" label="Nature of work" error={err('life_assured.nature_of_work')}>
          <Select id="la-work" value={String(la.nature_of_work)} options={v.nature_of_work} onChange={(x) => setL('nature_of_work', x)} />
        </Field>
        <Field id="la-income" label="Annual income (Rs)" error={err('life_assured.annual_income')}>
          <input id="la-income" className={inputClass} inputMode="numeric" value={String(la.annual_income)} onChange={(e) => setL('annual_income', e.target.value.replace(/[^0-9.]/g, ''))} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={Boolean(la.is_nri)} onChange={(e) => setL('is_nri', e.target.checked)} /> I am a non-resident Indian
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={Boolean(la.is_pep)} onChange={(e) => setL('is_pep', e.target.checked)} /> I hold, or held, a public office (PEP)
        </label>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Source of funds</legend>
        {funds.map((f, i) => (
          <div key={i} className="flex gap-2">
            <select aria-label={`Source of funds ${i + 1}`} className={inputClass} value={f.source} onChange={(e) => setFunds((c) => c.map((x, j) => (j === i ? { ...x, source: e.target.value } : x)))}>
              {v.fund_sources.map((o) => (
                <option key={o} value={o}>{words(o)}</option>
              ))}
            </select>
            <input aria-label={`Percent ${i + 1}`} className={`${inputClass} w-24`} inputMode="numeric" value={f.percent} onChange={(e) => setFunds((c) => c.map((x, j) => (j === i ? { ...x, percent: e.target.value.replace(/\D/g, '') } : x)))} />
            {funds.length > 1 && (
              <button type="button" className={secondaryButton} onClick={() => setFunds((c) => c.filter((_, j) => j !== i))}>Remove</button>
            )}
          </div>
        ))}
        <div className="flex items-center gap-3">
          {funds.length < 3 && (
            <button type="button" className={secondaryButton} onClick={() => setFunds((c) => [...c, { source: 'business', percent: '' }])}>Add a source</button>
          )}
          <span className={`text-xs ${fundTotal === 100 ? 'text-gray-500' : 'text-amber-700'}`}>Total {fundTotal}%</span>
        </div>
        {err('life_assured.fund_sources') && <p className="text-xs text-red-600" role="alert">{err('life_assured.fund_sources')}</p>}
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Address</legend>
        {(
          [
            ['house', 'House / flat'], ['street', 'Street'], ['landmark', 'Landmark'], ['city', 'City'], ['state', 'State'], ['pincode', 'PIN code'],
          ] as const
        ).map(([k, label]) => (
          <Field key={k} id={`addr-${k}`} label={label} error={err(`life_assured.address.${k}`)}>
            <input id={`addr-${k}`} className={inputClass} maxLength={k === 'pincode' ? 6 : 100} value={address[k]} onChange={(e) => setAddress((c) => ({ ...c, [k]: e.target.value }))} />
          </Field>
        ))}
      </fieldset>

      {nominees.map((n, i) => (
        <fieldset key={i} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Nominee {i + 1}</legend>
          <Field id={`n${i}-title`} label="Title" error={err(`nominees.${i}.title`)}>
            <Select id={`n${i}-title`} value={n.title} options={v.titles} onChange={(x) => setNominee(i, { title: x })} />
          </Field>
          <Field id={`n${i}-marital`} label="Marital status" error={err(`nominees.${i}.marital_status`)}>
            <Select id={`n${i}-marital`} value={n.marital_status} options={v.marital_status} onChange={(x) => setNominee(i, { marital_status: x })} />
          </Field>
          <Field id={`n${i}-first`} label="First name" error={err(`nominees.${i}.first_name`)}>
            <input id={`n${i}-first`} className={inputClass} value={n.first_name} onChange={(e) => setNominee(i, { first_name: e.target.value })} />
          </Field>
          <Field id={`n${i}-last`} label="Last name" error={err(`nominees.${i}.last_name`)}>
            <input id={`n${i}-last`} className={inputClass} value={n.last_name} onChange={(e) => setNominee(i, { last_name: e.target.value })} />
          </Field>
          <Field id={`n${i}-dob`} label="Date of birth" error={err(`nominees.${i}.date_of_birth`)}>
            <input id={`n${i}-dob`} type="date" className={inputClass} value={n.date_of_birth} onChange={(e) => setNominee(i, { date_of_birth: e.target.value })} />
          </Field>
          <Field id={`n${i}-gender`} label="Gender" error={err(`nominees.${i}.gender`)}>
            <Select id={`n${i}-gender`} value={n.gender} options={v.genders} onChange={(x) => setNominee(i, { gender: x })} />
          </Field>
          <Field id={`n${i}-rel`} label="Relationship to you" error={err(`nominees.${i}.relationship`)}>
            <Select id={`n${i}-rel`} value={n.relationship} options={v.nominee_relations} onChange={(x) => setNominee(i, { relationship: x })} />
          </Field>
          <Field id={`n${i}-share`} label="Share (%)" error={err(`nominees.${i}.share_pct`)}>
            <input id={`n${i}-share`} className={inputClass} inputMode="decimal" value={n.share_pct} onChange={(e) => setNominee(i, { share_pct: e.target.value.replace(/[^0-9.]/g, '') })} />
          </Field>
          {n.appointee && (
            <div className="sm:col-span-2 grid grid-cols-1 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40 sm:grid-cols-2">
              <p className="sm:col-span-2 text-xs text-gray-600 dark:text-gray-300">This nominee is under 18, so an adult appointee is needed.</p>
              <Field id={`n${i}-ap-title`} label="Appointee title" error={err(`nominees.${i}.appointee.title`)}>
                <Select id={`n${i}-ap-title`} value={n.appointee.title} options={v.titles} onChange={(x) => setNominee(i, { appointee: { ...n.appointee!, title: x } })} />
              </Field>
              <Field id={`n${i}-ap-marital`} label="Appointee marital status" error={err(`nominees.${i}.appointee.marital_status`)}>
                <Select id={`n${i}-ap-marital`} value={n.appointee.marital_status} options={v.marital_status} onChange={(x) => setNominee(i, { appointee: { ...n.appointee!, marital_status: x } })} />
              </Field>
              {(['first_name', 'last_name'] as const).map((k) => (
                <Field key={k} id={`n${i}-ap-${k}`} label={`Appointee ${k === 'first_name' ? 'first' : 'last'} name`} error={err(`nominees.${i}.appointee.${k}`)}>
                  <input id={`n${i}-ap-${k}`} className={inputClass} value={n.appointee![k]} onChange={(e) => setNominee(i, { appointee: { ...n.appointee!, [k]: e.target.value } })} />
                </Field>
              ))}
              <Field id={`n${i}-ap-dob`} label="Appointee date of birth" error={err(`nominees.${i}.appointee.date_of_birth`)}>
                <input id={`n${i}-ap-dob`} type="date" className={inputClass} value={n.appointee.date_of_birth} onChange={(e) => setNominee(i, { appointee: { ...n.appointee!, date_of_birth: e.target.value } })} />
              </Field>
              <Field id={`n${i}-ap-gender`} label="Appointee gender" error={err(`nominees.${i}.appointee.gender`)}>
                <Select id={`n${i}-ap-gender`} value={n.appointee.gender} options={v.genders} onChange={(x) => setNominee(i, { appointee: { ...n.appointee!, gender: x } })} />
              </Field>
              <Field id={`n${i}-ap-rel`} label="Appointee's relationship to the nominee" error={err(`nominees.${i}.appointee.relationship`)}>
                <Select id={`n${i}-ap-rel`} value={n.appointee.relationship} options={v.appointee_relations} onChange={(x) => setNominee(i, { appointee: { ...n.appointee!, relationship: x } })} />
              </Field>
            </div>
          )}
          {nominees.length > 1 && (
            <div className="sm:col-span-2">
              <button type="button" className={secondaryButton} onClick={() => setNominees((c) => c.filter((_, j) => j !== i))}>Remove nominee</button>
            </div>
          )}
        </fieldset>
      ))}
      <div className="flex items-center gap-3">
        {nominees.length < v.max_nominees && (
          <button type="button" className={secondaryButton} onClick={() => setNominees((c) => [...c, emptyNominee()])}>Add a nominee</button>
        )}
        <span className={`text-xs ${shareTotal === 100 ? 'text-gray-500' : 'text-amber-700'}`}>Shares total {shareTotal}%</span>
      </div>
      {err('nominees') && <p className="text-xs text-red-600" role="alert">{err('nominees')}</p>}

      {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}
      <div className="flex gap-3">
        <button type="submit" className={primaryButton} disabled={busy}>{busy ? 'Saving…' : 'Save my details'}</button>
        {onCancel && <button type="button" className={secondaryButton} onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
